import { debounce, DebouncedFunc } from 'lodash-unified';
import { Store } from '@betgames/bg-state-manager';
import { GameId } from '@bg-shared/enums';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { IBalanceState } from './interfaces';

class BalanceStore extends Store<Record<number, IBalanceState>> {
    private stagedAmounts: Record<GameId, number> = {} as Record<GameId, number>;

    private debouncedUpdateBalanceStore: DebouncedFunc<(data: IBalanceState) => void> = null;

    constructor() {
        super({
            key: 'Balance',
            default: {},
        });
    }

    public updateStagedBalance(gameId: GameId, amount: number, balance?: string): void {
        if (!partnerSettingsStore.isRealtimeBalanceEnabled) {
            return;
        }

        this.stagedAmounts[gameId] = amount;

        this.updateBalanceStore({
            balance: balance ?? this.value[0].balance,
        });
    }

    // Balance per gameId used only when isApiSendBalancePerGame is in partner response (PLS) otherwise is kept in 0 index
    private internalUpdateBalanceStore(data: IBalanceState): void {
        this.update((draft) => {
            draft[data.gameId || 0] = data;

            if (Object.values(this.stagedAmounts).length) {
                draft[0].stagedAmounts = { ...this.stagedAmounts };
            }
        });
    }

    public updateBalanceStore(data: IBalanceState): void {
        if (this.debouncedUpdateBalanceStore) {
            this.debouncedUpdateBalanceStore(data);
            return;
        }

        this.internalUpdateBalanceStore(data);
    }

    public waitForUpdate(delay = 0): void {
        if (delay) {
            this.debouncedUpdateBalanceStore?.cancel();
            this.debouncedUpdateBalanceStore = debounce(
                this.internalUpdateBalanceStore.bind(this),
                delay,
            );
            return;
        }

        this.debouncedUpdateBalanceStore?.cancel();
        this.debouncedUpdateBalanceStore = null;
    }
}

export const balanceStore = new BalanceStore();
